import { useEffect, useRef } from "react";

// Closes an open dropdown/menu/popover on an outside pointerdown or Escape.
// Attach the returned ref to the element that should NOT trigger a dismiss
// (the panel itself — not its trigger button, unless the trigger is inside
// the same ref'd container).
//
// `onDismiss` is read from a ref internally so callers can pass an inline
// closure without needing to memoize it — only `open` re-running the effect.
export function useDismissable<T extends HTMLElement>(
  open: boolean,
  onDismiss: () => void,
  opts?: { escape?: boolean },
) {
  const ref = useRef<T>(null);
  const dismissRef = useRef(onDismiss);
  const escape = opts?.escape ?? true;

  // Kept fresh in an effect rather than assigned during render. The listeners
  // below only ever read `.current` at event time, so they still see the
  // newest closure without the subscription effect having to re-run.
  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!open) return;

    function handleMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        dismissRef.current();
      }
    }
    // AN ESCAPE THIS PANEL ANSWERED IS SPENT — AND IT ANSWERS FIRST.
    //
    // `Dialog` closes on any Escape that reaches it without having been
    // `preventDefault()`ed, so a dropdown that dismisses itself silently lets
    // ONE keystroke close the dropdown AND the dialog behind it — the
    // composer's emoji picker over a half-written post. Saying "handled" is
    // what makes the innermost open thing the only one that answers.
    //
    // WHY CAPTURE. Both listeners sit on `document`, where order is
    // registration order — and the dialog was open first, so in the bubble
    // phase it would answer (and close) before this handler ever ran. A
    // capture-phase listener on `document` runs before every bubble listener
    // on it, whoever registered first; that, not the flag alone, is what makes
    // the ordering true. Four of the thirteen call sites reach this listener at
    // all — `EmojiPicker`, `MessagesDropdown`, `DmReactionSummary` and
    // `GroupNotificationBell` — and all four are popovers with nothing behind
    // them that wants the key; the other nine pass `escape: false` and never
    // register it. (`LocationPicker` is the one whose opt-out is load-bearing
    // rather than incidental: it pairs Escape with arrow-key result navigation
    // in its own `onKeyDown`.)
    //
    // The count matters, so recount it rather than trusting this sentence — it
    // has been wrong three times. An early version read as though the opt-out
    // were a single special case, which is the opposite of the ratio; the next
    // said "three" and had missed `GroupHeader`; and the one before this said
    // "five of fourteen" and still counted `GroupHeader`'s membership menu,
    // which OSO-641 moved onto `ActionMenu` — that file no longer imports this
    // hook at all.
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      dismissRef.current();
    }

    // mousedown (not click) matches every existing dismiss handler in the
    // codebase — switching to click/pointerdown would change dismiss timing
    // relative to the trigger's own click handler.
    document.addEventListener("mousedown", handleMouseDown);
    if (escape) document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      if (escape) document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open, escape]);

  return ref;
}
